<template>
  <div class="lots-table table-component">
    <artisio-table :pagination="tableData.pagination" selectable :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :index="false" :checkboxes="true"
                   :search="tableData.search" :enable-simple-search="false" :export-attributes="exportAttributes"
                   export-vuex-module-name="lots" export-permission-name="export_inventory" requires-min-height
                   @paramsChanged="getTableData" show-toggle-columns :extra-data="extraData"
                   @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns"
                   filter-toggle-string="lots-table" ref="lotTable" :enable-frontend-export="enableFrontendExport"
                   :persistent-selected-items="persistentSelectedItems" @selectedItemsAllChange="selectedItemsAllChange"
                   :persistent-selection-columns="persistentSelectionColumns">

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="lots-table">
          <lot-search :exclude-search-fields="excludeSearchFields" :sale="tableData ? tableData.sale : null"/>
        </search-collapse>
      </template>

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown class="btn-table-actions" :text="$t('Actions')" variant="primary"
                    v-permission="'change_inventoryitem'" v-if="!hideAllActions">
          <b-dropdown-item v-if="!hideAction(ACTION_PUBLISH_LOT)" v-permission="'publish_auction'"
                           :disabled="disableButton(selectedItems)" @click="onPublishClick(selectedItems)">
            {{ $t("Publish") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_LOT_UP)" v-permission="'change_inventoryitem'"
                           @click="onLotUpClick">
            {{ $t("Lot Up") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_LOT_UP_USING_QR)" v-permission="'change_inventoryitem'"
                           @click="onLotUpUsingQrClick">
            {{ $t("Lot Up Using QR") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_RESERVE_ITEMS) && !enableContinuousAuction"
                           v-permission="'change_inventoryitem'" @click="onAssignItemsClick"
                           :disabled="saleCompleted || saleArchived">
            {{ $t("Reserve Items") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_LOT_UP)" v-permission="'change_inventoryitem'"
                           :disabled="tableData.rows.length === 0"
                           @click="onBatchLotUpClick">
            {{ $t("Batch Lot Up") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_LOT_UP)" v-permission="'change_inventoryitem'"
                           :disabled="tableData.rows.length === 0"
                           @click="onBatchLotUpV2Click">
            {{ $t("Batch Lot Up V2") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="saleDraft && !hideAction(ACTION_BATCH_LOT_UP) && hasArtisioTimedProvider"
                           v-permission="'change_inventoryitem'"
                           :disabled="tableData.rows.length === 0"
                           @click="onBatchCategoryLotUpClick">
            {{ $t("Batch Category Lot Up") }}
          </b-dropdown-item>
          <input :multiple="false" @change="onLotUpUsingExcel"
                 accept=".xls,.xlsx" type="file" class="d-none"
                 ref="lotUpUsingExcel"
                 id="lot-up-using-excel"/>
          <b-dropdown-item v-permission="'change_inventoryitem'"
                           v-if="!hideAction(ACTION_LOT_UP_USING_EXCEL)"
                           @click="$refs.lotUpUsingExcel.click()">
            {{ $t("Lot Up Using Excel") }}
          </b-dropdown-item>

          <upload-images-dropdown-item :auction-uuid="tableData.sale.uuid" hide-icon
                                       v-if="tableData.sale && !enableContinuousAuction"/>

          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_UPDATE_SESSION) && enableTimedSessions && isTimed"
                           v-permission="'change_inventoryitem'" :disabled="disableButton(selectedItems)"
                           @click="onBatchUpdateSessionClick(selectedItems)">
            {{ $t("Batch Update Session") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_UPDATE_COLLECTION) && !enableContinuousAuction"
                           v-permission="'change_inventoryitem'" :disabled="disableButton(selectedItems)"
                           @click="onBatchUpdateCollectionClick(selectedItems)">
            {{ $t("Batch Update Collection") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_FINANCIAL_UPDATE) && !enableContinuousAuction"
                           v-permission="'change_inventoryitem'" :disabled="disableButton(selectedItems)"
                           @click="onBatchFinancialUpdateClick(selectedItems)">
            {{ $t("Batch Financial Update") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_UPDATE_HIDE_LOT)"
                           v-permission="'change_inventoryitem'" :disabled="disableButton(selectedItems)"
                           @click="onBatchHideShowClick(selectedItems)">
            {{ $t("Batch Hide/Show") }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="!hideAction(ACTION_BATCH_UPDATE_AUCTION_LOT_IMAGES)"
            v-permission="'change_inventoryitem'"
            :disabled="disableButton(selectedItems)"
            @click="onBatchAuctionImagesUpdateClick(selectedItems)">
            {{ $t("Select/Unselect Auction Gallery") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BULK_ADJUST_PRICE)" v-permission="'change_inventoryitem'"
                           :disabled="disableButton(selectedItems)"
                           @click="onBulkAdjustPriceClick(selectedItems)">
            {{ $t("Adjust Price") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_MARK_UNSOLD)" v-permission="'auction_results_auction'"
                           :disabled="disableButton(selectedItems) || saleArchived"
                           @click="onBatchMarkAsUnsoldClick(selectedItems)">
            {{ $t("Batch Mark Unsold") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_BATCH_WITHDRAW)" v-permission="'auction_results_auction'"
                           :disabled="disableButton(selectedItems)"
                           @click="onBatchWithdrawClick(selectedItems)">
            {{ $t("Batch Withdraw") }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_REASSIGN_SALE) && !enableContinuousAuction"
                           v-permission="'change_inventoryitem'"
                           :disabled="disableButton(selectedItems)"
                           @click="reassignReservations(selectedItems)">
            {{ $t('Reassign Sale') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_inventoryitem'"
                           :disabled="disableButton(selectedItems)"
                           @click="onNewCondRequestClick(selectedItems)">
            {{ $t('New Cond. Rep. Request') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!hideAction(ACTION_GENERATE_LOT_PRINTOUTS)"
                           v-permission="'change_inventoryitem'"
                           :disabled="disableButton(selectedItems) || generateLotPrintoutsLoading"
                           @click="onGeneratePdfPrintoutsClick(selectedItems)">
            <div class="d-flex justify-content-between align-items-center">
              <spinner class="mr-2 d-inline-block" :show="generateLotPrintoutsLoading" spinner-size="1"
                       :with-text="false"/>
              <span>
                 {{ $t("Generate PDF Printouts") }}
              </span>
            </div>
          </b-dropdown-item>
          <b-dropdown-item @click="onInventoryOverviewExportClick(selectedItems)" v-permission="'change_inventoryitem'"
                           :disabled="selectedItems.size === 0"
                           v-if="inventoryOverviewPrintsHpiData && !hideAction(ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW)">
            {{ $t('Inventory Overview') }}
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown class="btn-table-actions ml-2" :text="$t('Export Images')" variant="primary"
                    v-if="!hideAction(ACTION_EXPORT_LOT_IMAGES) && !enableContinuousAuction"
                    v-permission="'change_inventoryitem'">
          <b-dropdown-item @click="onLotsImagesSetNameTemplateClick">
            {{ $t('Export All Images') }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="!isSelectedExportEnabled(selectedItems)"
                           v-b-tooltip.hover.left="exportSelectedTooltip(selectedItems)"
                           @click="onExportSelectedImagesEntryClick(selectedItems)">
            {{ $t('Export Selected Images') }}
          </b-dropdown-item>
        </b-dropdown>
        <delete-button v-if="!hideAction(ACTION_DELETE_LOT)" v-permission="'delete_auction' " class="ml-2"
                       :disabled="disableButton(selectedItems) || saleArchived"
                       @click="onBatchRemoveClick(selectedItems)"/>
        <input type="file" @change="onImagesFileChange" ref="imagesFileRef" class="d-none" multiple="multiple"
               :accept="imageFileFormats.join(',')">
        <input type="file" @change="onZipFileChange" ref="zipFileRef" class="d-none"
               accept="zip,application/octet-stream,application/zip,application/x-zip,application/x-zip-compressed">
      </template>

      <template slot="cell(image)" slot-scope="{item}" class="text-center">
        <list-image-uploader v-if="item.inventory" :inventory="item.inventory" @imagesUploaded="onImagesUploaded"/>
      </template>

      <template slot="cell(stock_no)" slot-scope="{item}">
        <router-link v-if="$hasPermission('view_receipt')"
                     :to="{name: 'receipt.view', params: {id: item.inventory.receipt_uuid}}">
          {{ item.inventory.receipt.receipt_no }}
        </router-link>
        /
        <router-link v-if="$hasPermission('view_inventoryitem')"
                     :to="{ name: 'inventory.view', params: { id: item.inventory_uuid }}">
          {{ item.inventory.item_no }}
        </router-link>


        <b-tooltip :target="`group-item-span-${item.uuid}`" :title="$t('Group Item')"/>
        <span v-if="item.inventory.is_grouped_item" class="badge badge-primary ml-2"
              :id="`group-item-span-${item.uuid}`">
            <font-awesome-icon icon="boxes" class="icon"/>
            {{ item.inventory.source_inventories_count }}
        </span>
        <action-buttons
          :edit="!saleArchived"
          :remove="!saleArchived"
          :view-permission="'view_inventoryitem'"
          :delete-permission="'delete_inventoryitem'"
          :edit-permission="'change_inventoryitem'"
          @viewClick="onViewClick(item)"
          @editClick="onEditClick(item)"
          @deleteClick="onRemoveClick(item)"
        />
      </template>

      <template slot="cell(lot_no)" slot-scope="{item}">
        <a v-if="enableContinuousAuction && item.auction && item.auction.live_url && getStatus(item) === 'published'"
           target='_blank' :href="item.auction.live_url">
          {{ item.lot_no ? item.lot_no : '' }}
        </a>
        <span v-else>{{ item.lot_no ? item.lot_no : '' }}</span>
      </template>

      <template slot="cell(title)" slot-scope="{item}">
        <div v-if="item.inventory.is_draft">
          <span class="badge badge-secondary">{{ $t('Draft') }}</span>
        </div>
        {{ item.inventory.short_description | striptags }}
      </template>

      <template slot="cell(date)" slot-scope="{item}">
        {{ getAuctionDate(item, 'start_date') | toDateTime }} -
        <span v-if="getAuctionDate(item, 'end_date')">
        {{ getAuctionDate(item, 'end_date') | toDateTime }}
        </span>
        <span v-else>
          {{ $t('NaN') }}
        </span>
      </template>

      <template slot="cell(sold_via_negotiation)" slot-scope="{item}">
        <span class="badge badge-success" v-if="item.lot && item.lot.sold_via_negotiation">{{ $t("Yes") }}</span>
        <span class="badge badge-danger" v-else>{{ $t("No") }}</span>
      </template>

      <template slot="cell(hide_lot)" slot-scope="{item}">
        <span class="badge badge-success" v-if="item.lot && item.lot.hide_lot">{{ $t("Yes") }}</span>
        <span class="badge badge-danger" v-else>{{ $t("No") }}</span>
      </template>

      <template slot="cell(reserve)" slot-scope="{item}">
        {{ item.reserve | asCurrency(getCurrency(item)) }}
      </template>

      <template slot="cell(hammer_price)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.hammer_price">
          {{ item.lot.hammer_price | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(hammer_bp_incl_vat)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.financial_item">
          {{ hammerBpInclVat(item) | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(lot_handling_charge)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.financial_item">
          {{ item.lot.financial_item.lot_handling_charge | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(start_price)" slot-scope="{item}">
        {{ item.start_price | asCurrency(getCurrency(item)) }}
      </template>

      <template slot="cell(auction_date_uuid)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.auction_date">
          {{ item.lot.auction_date.title }}
        </span>
      </template>

      <template slot="cell(auction_collection_uuid)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.auction_collection">
          {{ item.lot.auction_collection.translations | attributeByMasterLanguage('title') }}
        </span>
      </template>

      <template slot="cell(low)" slot-scope="{item}">
        <template v-if="item.low && item.high">
          {{ item.low | asCurrency(getCurrency(item)) }} -
          {{ item.high | asCurrency(getCurrency(item)) }}
        </template>
      </template>

      <template slot="cell(winning_bidder)" slot-scope="{item}">
        <router-link v-if="item.lot && item.lot.bidder && $hasPermission('view_customer')"
                     :to="{ name: 'customers.dashboard', params: { id: item.lot.bidder.customer_uuid }}">
          {{ item.lot.bidder.paddle_number }} - {{ item.lot.bidder.customer.display_name }}
        </router-link>
        <p class="m-0" v-else-if="item.lot && item.lot.bidder">
          {{ item.lot.bidder.paddle_number }} - {{ item.lot.bidder.customer.display_name }}
        </p>
      </template>

      <template slot="cell(negotiation_status)" slot-scope="{item}">
        <span v-as-status="item.negotiation_status">
          {{ item.negotiation_status || $t("N/A")  | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
      </template>

      <template slot="cell(recommended_by_user)" slot-scope="{item}">
        <template v-if="item.inventory && item.inventory.receipt && item.inventory.receipt.recommended_by_user">
          {{ item.inventory.receipt.recommended_by_user.display_name }}
        </template>
      </template>

      <template slot="cell(registration_vrm)" slot-scope="{item}">
        <template v-if="item.inventory && item.inventory.dynamic_fields">
          {{ item.inventory.dynamic_fields | attributeByMasterLanguage('registration_vrm') | striptags }}
        </template>
      </template>

      <template slot="cell(status)" slot-scope="{item}">
        <span v-as-status="item.status">{{ getStatus(item) | removeUnderscores | toUpperCaseFirst | translate }}</span>
      </template>


      <template slot="cell(invoice)" slot-scope="{item}">
        <span v-if="invoice(item)" v-as-status="invoice(item).status" @click="onInvoiceClick(item)" class="invoice">
          {{ invoice(item).invoice_no }} - {{ invoice(item).status | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
        <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
      </template>

      <template slot="cell(settlement)" slot-scope="{item}">
        <span v-if="settlement(item)" v-as-status="settlement(item).status" @click="onSettlementClick(item)"
              class="invoice">
          {{
            settlement(item).settlement_no
          }} - {{ settlement(item).status | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
        <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
      </template>

      <template slot="cell(account_managers)" slot-scope="{item}">
        <template v-if="item.inventory.account_managers && item.inventory.account_managers.length > 0">
          {{ item.inventory.account_managers[0].display_name }}
        </template>
      </template>

      <template slot="cell(cataloguing_lifecycle)" slot-scope="{item}">
        <template v-if="item.inventory.cataloguing_lifecycle">
          <cataloguing-lifecycle-display-widget :inventory="item.inventory"/>
        </template>
      </template>

      <template slot="cell(photography_lifecycle)" slot-scope="{item}">
        <template v-if="item.inventory.photography_lifecycle">
          <photography-lifecycle-display-widget :inventory="item.inventory"/>
        </template>
      </template>

      <template slot="cell(created_data)" slot-scope="{item}">
        {{ item.created_at | toDate }} by {{ item.created_by.display_name }}
      </template>

      <template slot="cell(updated_data)" slot-scope="{item}">
        {{ item.updated_at | toDate }} by {{ item.updated_by.display_name }}
      </template>

      <template slot="cell(actions)" slot-scope="{item}">
        <b-tooltip :target="`item-negotiation-btn-${item.uuid}`" :title="$t('Negotiations')"/>
        <b-button v-if="['lotted', 'sold', 'unsold'].includes(item.status)" :id="`item-negotiation-btn-${item.uuid}`"
                  variant="primary" size="sm" class="ml-2" @click="onNegotiationsModalOpenClick(item)"
                  v-permission="'auction_results_auction'">
          <font-awesome-icon icon="file-alt" class="icon"/>
        </b-button>
      </template>

      <template slot="cell(customer)" slot-scope="{item}">
        <router-link :to="{ name: 'customers.dashboard', params: { id: item.inventory.customer.uuid }}">
          {{ item.inventory.customer.display_name_with_company }}
        </router-link>
      </template>

      <template slot="cell(sale_no)" slot-scope="{item}">
        <router-link v-if="$hasPermission('change_auction') && !enableContinuousAuction"
                     :to="{name: 'sales.view', params: {id: item.auction.uuid}}">
          {{ item.auction.sale_no }}
        </router-link>
        <span v-else>
          {{ item.auction.sale_no }}
        </span>
      </template>

      <template slot="cell(winner_bid)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.winner_bid">
          {{ item.lot.winner_bid.amount | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(highest_bid)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.highest_bid">
          {{ item.lot.highest_bid.amount | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(collected)" slot-scope="{item}">
        <span v-if="item.collected" class="badge-success badge">{{ $t("Yes") }}</span>
        <span v-else class="badge-danger badge">{{ $t("No") }}</span>
      </template>

      <template slot="cell(department)" slot-scope="{item}">
        <span v-if="item.inventory && item.inventory.department">
          {{ item.inventory.department.name | masterLanguage }}
        </span>
      </template>

      <template slot="cell(category)" slot-scope="{item}">
        <span v-if="item.inventory && item.inventory.category">
          {{ item.inventory.category.constructed_full_path | masterLanguage }}
        </span>
      </template>

      <template slot="cell(reserve_type)" slot-scope="{item: inventory}">
        {{ getReserveType(inventory.reserve_type) }}
      </template>

      <template slot="cell(specialists)" slot-scope="{item: item}">
        <template v-if="item.inventory.specialists">
          {{ item.inventory.specialists.map(s => s.display_name).join(', ') }}
        </template>
      </template>

      <template slot="cell(rep_user)" slot-scope="{item: item}">
        <span v-if="item.inventory && item.inventory.rep_user">{{ item.inventory.rep_user.display_name }}</span>
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span :key="`${item.uuid}-${dynamicField}`"
              v-html="$options.filters.asDynamicField($options.filters.masterLanguage(item.inventory.dynamic_fields)[dynamicField], field.type, field.choices)">
        </span>
      </template>

      <template #cell(total_bids)="{item: reservation, toggleDetails}">
        <a v-if="reservation.lot && reservation.lot.total_bids" href="javascript:void(0)"
           @click="toggleDetails">
          {{ reservation.lot.total_bids }} {{ reservation.lot.total_bids === 1 ? $t('bid') : $t('bids') }}
        </a>
        <span v-else>
          0 {{ $t('bid') }}
        </span>
      </template>
      <template #row-details="{item}">
        <simple-bids-table :lot-uuid="item.lot.uuid" :currency="getCurrency(item)"/>
      </template>
      <template slot="cell(condition_requests_count)" slot-scope="{item}">
        <span v-if="item">
          {{ item.condition_requests_count }}
        </span>
      </template>
      <template slot="cell(condition_requests_sent)" slot-scope="{item}">
        <span v-if="item">
          {{ item.condition_requests_sent }}
        </span>
      </template>

      <template slot="cell(use_images_for_auction_gallery)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.use_images_for_auction_gallery" class="badge-success badge">{{
            $t("Yes")
          }}</span>
        <span v-else class="badge-danger badge">{{ $t("No") }}</span>
      </template>

      <template slot="cell(receipt_contract_status)" slot-scope="{item}">
        <span v-if="item.inventory.receipt.receipt_contract_status"
              v-as-status="item.inventory.receipt.receipt_contract_status">
          {{ item.inventory.receipt.receipt_contract_status | removeUnderscores | toUpperCaseEveryWord | translate }}
        </span>
        <span v-as-status="item.inventory.receipt.receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </template>

      <template slot="cell(pre_receipt_contract_status)" slot-scope="{item}">
        <span v-if="item.inventory.receipt.pre_receipt_contract_status"
              v-as-status="item.inventory.receipt.pre_receipt_contract_status">
          {{ item.inventory.receipt.pre_receipt_contract_status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
        <span v-as-status="item.inventory.receipt.pre_receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </template>

      <template slot="cell(item_location)" slot-scope="{item}">
        <span v-if="item.inventory && item.inventory.item_location_uuid">
          {{ getItemLocationComputedName(item.inventory) }}
          <a href="#" v-if="priorMoveCount(item.inventory) > 0" class="ml-1 badge badge-light border"
             @click.prevent="onMovementHistoryBadgeClick(item.inventory)">
            +{{ priorMoveCount(item.inventory) }}
          </a>
        </span>
      </template>

      <template slot="cell(source_of_sale)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.source_of_sale" v-as-status>
          {{ item.lot.source_of_sale | removeUnderscores | toUpperCaseEveryWord }}
        </span>
      </template>

      <template slot="cell(dpv_pdf)" slot-scope="{item}">
        <b-button v-if="item && item.dpv_pdf" @click="onViewDpvPdfClick(item.dpv_pdf)" variant="primary"
                  size="sm" class="mr-2">
          <font-awesome-icon icon="download"/>
        </b-button>
      </template>

      <template slot="cell(proposal_suggestion)" slot-scope="{item}">
        <span v-if="item.proposal_suggestion"
              v-as-status="item.proposal_suggestion">
          {{ item.proposal_suggestion | proposalStatusSuggestionMap }}
        </span>
      </template>

      <template slot="cell(proposal_status)" slot-scope="{item}">
        <span v-if="item.proposal_status"
              v-as-status="`proposal_${item.proposal_status}`">
          {{ item.proposal_status | proposalStatusSuggestionMap }}
        </span>
      </template>

      <template slot="cell(new_low)" slot-scope="{item}">
        <span v-if="item.new_low">
          {{ item.new_low | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(new_high)" slot-scope="{item}">
        <span v-if="item.new_high">
          {{ item.new_high | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(new_reserve)" slot-scope="{item}">
        <span v-if="item.new_reserve">
          {{ item.new_reserve | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(new_start_price)" slot-scope="{item}">
        <span v-if="item.new_start_price">
          {{ item.new_start_price | asCurrency(getCurrency(item)) }}
        </span>
      </template>

      <template slot="cell(proposal_sent_by)" slot-scope="{item}">
        {{ item.proposal_sent_by ? item.proposal_sent_by.display_name : '' }}
      </template>

      <template slot="cell(proposal_sent_at)" slot-scope="{item}">
        {{ item.proposal_sent_at | toDate }}
      </template>

      <template slot="cell(proposal_confirmed_at)" slot-scope="{item}">
        {{ item.proposal_confirmed_at | toDate }}
      </template>

      <template slot="cell(proposal_confirmed_by)" slot-scope="{item}">
        {{ item.proposal_confirmed_by ? item.proposal_confirmed_by.display_name : '' }}
      </template>

      <template slot="cell(transfer_info)" slot-scope="{item}">
        <transfer-note-short-info :item="item" :source="SOURCE_LOTS_TABLE"/>
      </template>

      <template slot="cell(tax_status)" slot-scope="{item}">
        {{ item.lot ? item.lot.financial_item.tax_status_uuid : item.inventory.tax_status_uuid | asTaxStatus }}
      </template>

      <template slot="cell(new_reserve_type)" slot-scope="{item}">
        <template v-if="item.new_reserve_type">
          {{ getReserveType(item.new_reserve_type) }}
        </template>
      </template>

    </artisio-table>
    <qr-lot-up-modal/>
    <lot-up-modal/>
    <negotiation-modal/>
    <reassign-modal/>
    <reserve-items-modal/>
    <lot-export-images-templates-modal/>
    <unsold-modal/>
    <movement-history-modal :visible="movementHistoryModal.visible"
                            :inventory-uuid="movementHistoryModal.inventoryUuid"
                            @hidden="movementHistoryModal.visible = false"/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import LotsService from "./LotsService";
  import LotUpModal from "./modals/LotUpModal.vue";
  import InventoryService from "../inventory/InventoryService";
  import {
    ACTION_ARCHIVE_LOT,
    ACTION_RESERVE_ITEMS,
    ACTION_BATCH_LOT_UP,
    ACTION_DELETE_LOT,
    ACTION_GENERATE_LOT_PRINTOUTS,
    ACTION_LOT_EXPORT_ALL_IMAGES,
    ACTION_LOT_UP,
    ACTION_LOT_UP_USING_QR,
    ACTION_LOT_UPLOAD_IMAGES,
    ACTION_LOT_UPLOAD_ZIP,
    ACTION_MARK_UNSOLD,
    ACTION_PUBLISH_LOT,
    ACTION_REVERT_TO_COMPLETED_LOT,
    ACTION_SYNC_LOTS,
    LOTS_TABLE_NAME,
    SOURCE_LOTS_TABLE,
    ACTION_BULK_ADJUST_PRICE,
    ACTION_REASSIGN_SALE,
    WS_BULK_ADJUST_PRICES,
    WS_INVENTORY_BATCH_LOT_UP_PROGRESS,
    ACTION_LOT_UP_USING_EXCEL,
    ACTION_BATCH_UPDATE_SESSION,
    ACTION_BATCH_UPDATE_COLLECTION,
    ACTION_BATCH_UPDATE_HIDE_LOT,
    ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW,
    ACTION_BATCH_UPDATE_AUCTION_LOT_IMAGES, ACTION_BATCH_FINANCIAL_UPDATE,
    WS_LOTS_BATCH_MARK_UNSOLD_PROGRESS, ACTION_BATCH_MARK_UNSOLD,
    ACTION_BATCH_WITHDRAW,
    ACTION_EXPORT_LOT_IMAGES
  } from "../constants";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import LotSearch from "./LotSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import i18n from "../../shared/i18n";
  import ListImageUploader from "../inventory/ListImageUploader";
  import {Utils} from "@/core/services/utils.service";
  import QrLotUpModal from "@/modules/lots/modals/QrLotUpModal.vue";
  import DeleteButton from "../../core/components/common/DeleteButton";
  import {Formatter} from "@/core/services/formatter.service";
  import NegotiationModal from "@/modules/lots/modals/NegotiationModal.vue";
  import {
    LOTS_TABLE_CLEAR_SELECTED,
    ON_PDF_MODAL_OPEN,
    ON_PDF_MODAL_DATA_RETRIEVE
  } from "@/plugins/event-bus/event-bus-names";
  import Spinner from "@/core/components/common/Spinner";
  import ReassignModal from "@/modules/lots/modals/ReassignModal.vue";
  import SimpleBidsTable from "@/modules/lots/components/SimpleBidsTable";
  import ReserveItemsModal from "@/modules/lots/modals/ReserveItemsModal.vue";
  import {ProgressMixin} from "@/core/mixins/progress.mixin";
  import cloneDeep from "lodash/cloneDeep";
  import LotExportImagesTemplatesModal from "@/modules/lots/LotExportImagesNameTemplatesModal.vue"
  import UnsoldModal from "@/modules/lots/modals/UnsoldModal.vue";
  import AddNewButton from "@/core/components/common/AddNewButton.vue";
  import EventBus from "@/plugins/event-bus";
  import DpvService from "@/modules/dpv/DpvService";
  import SalesService from "@/modules/sales/SalesService";
  import TransferNoteShortInfo from "@/modules/inventory/components/TransferNoteShortInfo";
  import CataloguingLifecycleDisplayWidget from "@/core/widgets/CataloguingLifecycleDisplayWidget.vue";
  import PhotographyLifecycleDisplayWidget from "@/core/widgets/PhotographyLifecycleDisplayWidget.vue";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";
  import UploadImagesDropdownItem from "@/modules/inventory/UploadImagesDropdownItem.vue";
  import MovementHistoryModal from "@/modules/inventory/view/movement-history/MovementHistoryModal";

  const {mapActions, mapState} = createNamespacedHelpers('lots');
  const {mapActions: mapInventoryActions} = createNamespacedHelpers('inventory');
  const {mapActions: mapInvoiceActions} = createNamespacedHelpers('invoices');
  const {mapActions: mapSettlementsActions} = createNamespacedHelpers('settlements');
  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');
  const {mapActions: mapSalesActions} = createNamespacedHelpers('sales');

  const EXPORT_SELECTED_LOTS_LIMIT = 200;

  export default {
    name: "LotsTable",
    components: {
      PhotographyLifecycleDisplayWidget,
      CataloguingLifecycleDisplayWidget,
      UploadImagesDropdownItem,
      TransferNoteShortInfo,
      AddNewButton,
      UnsoldModal,
      SimpleBidsTable,
      Spinner,
      ReserveItemsModal,
      NegotiationModal,
      ReassignModal,
      QrLotUpModal,
      ListImageUploader,
      SearchCollapse,
      LotSearch,
      ActionButtons,
      LotUpModal,
      LotExportImagesTemplatesModal,
      ArtisioTable,
      DeleteButton,
      MovementHistoryModal
    },
    mixins: [ProgressMixin],
    props: {
      hideAllActions: {
        type: Boolean,
        default: false
      },
      hideActions: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
      persistentSelectedItems: {
        type: Boolean,
        default: false
      },
    },
    data: () => {
      return {
        generateLotPrintoutsLoading: false,
        isUploading: false,
        selectedItems: new Set(),
        movementHistoryModal: {
          visible: false,
          inventoryUuid: null,
        },
        exportAttributes: {
          'inventory.receipt.receipt_no': i18n.t('Receipt'),
          'inventory.item_no': i18n.t('Item'),
          'inventory.short_description': i18n.t('Title'),
          reserve: i18n.t('Reserve'),
          'inventory.receive_date': i18n.t('Receive Date'),
          status: i18n.t('Status'),
        },
        validReassignStatuses: ['reserved', 'unsold', 'withdrawn'],
        ACTION_BATCH_LOT_UP,
        ACTION_BULK_ADJUST_PRICE,
        ACTION_BATCH_UPDATE_SESSION,
        ACTION_BATCH_UPDATE_COLLECTION,
        ACTION_BATCH_FINANCIAL_UPDATE,
        ACTION_BATCH_UPDATE_HIDE_LOT,
        ACTION_DELETE_LOT,
        ACTION_LOT_EXPORT_ALL_IMAGES,
        ACTION_LOT_UP,
        ACTION_PUBLISH_LOT,
        ACTION_SYNC_LOTS,
        ACTION_ARCHIVE_LOT,
        ACTION_REVERT_TO_COMPLETED_LOT,
        ACTION_LOT_UP_USING_QR,
        ACTION_LOT_UPLOAD_IMAGES,
        ACTION_LOT_UPLOAD_ZIP,
        ACTION_MARK_UNSOLD,
        ACTION_GENERATE_LOT_PRINTOUTS,
        ACTION_REASSIGN_SALE,
        ACTION_RESERVE_ITEMS,
        ACTION_EXPORT_LOT_IMAGES,
        ACTION_LOT_UP_USING_EXCEL,
        ACTION_BATCH_MARK_UNSOLD,
        ACTION_BATCH_WITHDRAW,
        ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW: ACTION_INVENTORY_EXPORT_INVENTORY_OVERVIEW,
        ACTION_BATCH_UPDATE_AUCTION_LOT_IMAGES,
        progress: null,
        batchLotUpProgress: null,
        batchMarkUnsoldProgress: null,
        SOURCE_LOTS_TABLE: SOURCE_LOTS_TABLE,
        persistentSelectionColumns: [
          {
            name: i18n.t('Lot No'),
            displayFn: (item) => {
              return item.lot_no
            }
          },
          {
            name: i18n.t('Stock No'),
            displayFn: (item) => {
              return item.inventory ? item.inventory.stock_no : ''
            }
          },
          {
            name: i18n.t('Title'),
            displayFn: (item) => {
              return item.inventory ? Formatter.truncate(Formatter.stripTags(item.inventory.short_description), 50) : ''
            }
          },
        ]
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource
      }),
      ...mapStateGlobal({
        imageFileFormats: state => state.imageFileFormats,
        imageMaxFileSize: state => state.imageMaxFileSize,
        imagesZipMaxFileSize: state => state.imagesZipMaxFileSize,
        bulkImagesMaxFileCount: state => state.bulkImagesMaxFileCount,
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        enableContinuousAuction: state => state.enableContinuousAuction,
        oldLotPrintoutsEnabled: state => state.oldLotPrintoutsEnabled,
        currentUser: state => state.currentUser,
        auctionImageExportNamingFormat: state => state.auctionImageExportNamingFormat,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        enableTimedSessions: state => state.enableTimedSessions,
        reserveTypes: state => state.reserveTypes,
        inventoryOverviewPrintsHpiData: state => state.inventoryOverviewPrintsHpiData,
        locales: state => state.locales,
        batchUnsoldParams: state => state.batchUnsoldParams,
        disableItemEditOnSold: state => state.disableItemEditOnSold,
        itemLocations: state => state.itemLocations,
      }),
      itemLocationMap() {
        if (!this.itemLocations || !this.itemLocations.length) return {};
        const map = {};
        const buildMap = (nodes) => {
          for (const node of nodes) {
            map[node.uuid] = node.computed_name;
            if (node.children && node.children.length) buildMap(node.children);
          }
        };
        buildMap(this.itemLocations);
        return map;
      },
      tableFields() {
        return cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.computedExcludeColumns.includes(field.key) && field.isVisible)
      },
      computedExcludeColumns() {
        const excludeColumns = cloneDeep(this.excludeColumns);
        if (!this.enableInventoryQuantity) {
          excludeColumns.push('quantity');
        }
        return excludeColumns;
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.computedExcludeColumns,
          tableName: `${LOTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
      isTimed() {
        return this.tableData.sale && this.tableData.sale.type === 'timed';
      },
      saleDraft() {
        return this.tableData.sale && this.tableData.sale.status === 'draft';
      },
      saleArchived() {
        return this.tableData.sale && this.tableData.sale.status === 'archived';
      },
      saleCompleted() {
        return this.tableData.sale && this.tableData.sale.status === 'completed';
      },
      amountLotImagesName() {
        if (this.auctionImageExportNamingFormat && this.auctionImageExportNamingFormat.length) {
          return this.auctionImageExportNamingFormat.length;
        }
        return null
      },

      lotHasImages() {
        if (this.tableData && this.tableData.rows) {
          let lots = this.tableData.rows.filter(item => item.inventory.images && item.inventory.images.length);
          return !!lots.length;
        }
        return false
      }
    },
    methods: {
      ...mapActions([
        'showBatchLotUnsoldModal',
        'getTableData',
        'showModal',
        'removeLot',
        'updateListLotImages',
        'showReservationModal',
        'showQrLotUpModal',
        'showLotImagesNameModal',
        'getModalObject',
        'batchRemoveReservations',
        'batchMarkLotsUnsold',
        'updateSaleStatus',
        'showNegotiationModal',
        'updateToggleColumns',
        'showReassignModal',
        'showReserveItemsModal',
        'showBulkAdjustPriceModal',
        'showBatchSessionUpdateModal',
        'showBatchCollectionUpdateModal',
        'batchUpdateHideLot',
        'batchUpdateAuctionLotImages',
        'showBatchFinancialUpdateModal'
      ]),
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...mapInvoiceActions({
        updateInvoiceSearchModel: 'updateSearchModel'
      }),
      ...mapSettlementsActions({
        updateSettlementSearchModel: 'updateSearchModel'
      }),
      ...mapInventoryActions(['showInventoryOverviewGenerationModal', 'showConditionalReportRequestModal']),
      ...mapSalesActions(['getSale']),
      getItemLocationComputedName(inventory) {
        if (!inventory || !inventory.item_location_uuid) return '';
        return this.itemLocationMap[inventory.item_location_uuid] || (inventory.item_location ? inventory.item_location.name : '');
      },
      priorMoveCount(inventory) {
        const total = inventory && inventory.movement_history_count ? inventory.movement_history_count : 0;
        return total > 1 ? total - 1 : 0;
      },
      onMovementHistoryBadgeClick(inventory) {
        this.movementHistoryModal = {
          visible: true,
          inventoryUuid: inventory.uuid,
        };
      },
      getReserveType(reserveType) {
        const tmpType = this.reserveTypes.find(r => r.value === reserveType)
        return tmpType ? tmpType.key : ""
      },
      getStatus(item) {
        if (item.status.includes('lotted') && this.enableContinuousAuction) {
          if (item.auction && item.auction.status.includes('published')) {
            return item.auction.status;
          } else {
            return item.status;
          }
        }
        return item.status;
      },
      hammerBpInclVat(item) {
        return (parseFloat(item.lot.financial_item.hammer_bp_incl_vat) || 0);
      },
      invoice(item) {
        if (item.lot && item.lot.financial_item && item.lot.financial_item.invoice) {
          return item.lot.financial_item.invoice;
        }
        return null;
      },
      settlement(item) {
        if (item.lot && item.lot.financial_item && item.lot.financial_item.settlement) {
          return item.lot.financial_item.settlement;
        }
        return null;
      },
      async onInvoiceClick(item) {
        const params = {
          searchModel: {invoice_no: this.invoice(item).invoice_no},
          source: 'invoices',
        };
        await this.updateInvoiceSearchModel(params);
        await this.$router.push({name: 'invoices'});
      },
      async onSettlementClick(item) {
        const params = {
          searchModel: {settlement_no: this.settlement(item).settlement_no},
          source: 'settlements',
        };
        await this.updateSettlementSearchModel(params);
        await this.$router.push({name: 'settlements'});
      },
      onLotUpClick() {
        this.showModal({
          object: {},
          sale: this.tableData.sale,
          source: SOURCE_LOTS_TABLE
        });
      },
      onPublishClick(items) {
        this.saleStatusUpdate('publish', items);
      },
      onArchiveClick(items) {
        this.saleStatusUpdate('archive', items);
      },
      onRevertToCompletedClick(items) {
        this.saleStatusUpdate('revert-to-completed', items);
      },
      async saleStatusUpdate(status, items) {
        let formattedStatus = Formatter.removeDashes(status)
        let params = {
          auction_uuids: [],
        };
        items.forEach(item => {
          if (!params.auction_uuids.includes(item.auction_uuid)) {
            params.auction_uuids.push(item.auction_uuid);
          }
        });

        let success = await this.$confirm(this.$i18n.t(`Are you sure you want to {status} selected lots?`, {status: formattedStatus}));
        if (success) {
          const {success, body} = await this.updateSaleStatus({status, params});
          if (success) {
            if (body.errors.length > 0) {
              const listItemsArr = [];
              body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            } else {
              this.$toast('success', this.$i18n.t(`Lot(s) were successfully {status}`, {status: ['publish', 'archive'].includes(status) ? `${formattedStatus}ed` : formattedStatus}));
            }
          }
        }
      },
      async onSyncClick() {
        const {success, body} = await LotsService.syncLotsWithTimedAuction();
        if (success) {
          this.$toast('success', this.$i18n.t('Successfully synced'));
          this.getTableData()
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0], this.$i18n.t('Sync Validation'));
          }
        }
      },
      onBatchLotUpClick() {
        this.$router.push({name: 'sale-batch-lot-up', params: {sale_no: this.tableData.sale.sale_no}})
      },
      onBatchLotUpV2Click() {
        this.$router.push({name: 'sale-batch-lot-up-v2', params: {sale_no: this.tableData.sale.sale_no}})
      },
      onBatchCategoryLotUpClick() {
        this.$router.push({name: 'sale-batch-category-lot-up', params: {sale_no: this.tableData.sale.sale_no}})
      },
      onBatchAuctionImagesUpdateClick(selectedItems) {
        const reservation_uuids = Array.from(selectedItems).map(el => el.uuid)
        this.$confirm(
          this.$i18n.t("Are you sure you want to update 'Use Images for Auction Gallery' property for selected lots?"),
          this.$i18n.t("Lots were successfully updated"),
          this.batchUpdateAuctionLotImages,
          {reservation_uuids: reservation_uuids, use_images_for_auction_gallery: true},
          this.$i18n.t('Select/Unselect Auction Gallery'),
          i18n.t("Select"),
          i18n.t("Cancel"),
          [{
            handler: this.batchUpdateAuctionLotImages,
            handlerParams: {reservation_uuids: reservation_uuids, use_images_for_auction_gallery: false},
            submitBtnText: i18n.t("Unselect"),
            successMessage: this.$i18n.t("Lots were successfully updated")
          }]
        );
      },
      async onLotUpUsingExcel($event) {
        const files = $event.target.files;
        if (files.length === 0) {
          return false;
        }
        const formData = new FormData();
        formData.append('file', files[0]);
        let response = await LotsService.uploadLotUpFile(formData);
        if (response.success) {
          this.$toast('success', this.$i18n.t('Lot up has been successfully started.'));
        } else {
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => (listItemsArr.push(this.$createElement('li', {}, err))));
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), 'Lot Up');
          } else if (response.toastError) {
            this.$errorDialog(response.toastError)
          }
        }
        this.$refs.lotUpUsingExcel.value = null;
      },
      async onEditClick(item) {
        let tmpSale = this.enableContinuousAuction ? item.auction : this.tableData.sale
        if (!tmpSale) {
          let tmpResponse = await SalesService.getSale(item.auction_uuid, 'auction_collections,dates,currency')
          if (tmpResponse.success) {
            tmpSale = tmpResponse.body
          }
        }
        this.showReservationModal({sale: tmpSale, source: SOURCE_LOTS_TABLE});
        this.getModalObject({uuid: item.uuid, source: SOURCE_LOTS_TABLE});
      },
      async onBatchMarkAsUnsoldClick(items) {
        let uuids = [];
        for (let lot of items) {
          if (!['lotted', 'sold'].includes(lot.status)) {
            this.$toast('danger', 'Only lots with status of Sold and Lotted can be changed to Unsold')
            return
          }
          if (lot.status === 'lotted' && ((lot.auction && lot.auction.type === 'timed') || (this.tableData.sale && this.tableData.sale.type === 'timed'))) {
            this.$toast('danger', 'Only lots with status of Sold can be changed to Unsold for timed auctions')
            return
          }
          uuids.push(lot.uuid);
        }

        const expands = [
          'inventory',
          'lot.financial_item.settlement.payments_amount',
          'lot.financial_item.invoice.total_amount',
          'lot.financial_item.invoice.total_lots',
          'lot.financial_item.invoice.paid_amount',
          'lot.financial_item.invoice.invoice_payments.payment.currency',
          'lot.financial_item.settlement.settlement_payments.payment.currency',
          'lot.financial_item.settlement.settlement_payments.payment.batch_payment',
          'lot.financial_item.settlement.settlement_payments.payment.beneficiary_relation.related_customer',
          'lot.financial_item.settlement.settlement_payments.reference_number',
          'lot.financial_item.settlement.settlement_payments.check_number',
          'lot.financial_item.settlement.settlement_payments.check_uuid',
          'lot.financial_item.settlement.settlement_payments.check_status',
          'lot.financial_item.settlement.settlement_payments.payee',
          'lot.financial_item.settlement.settlement_payments.batch_id',
          'lot.financial_item.settlement.settlement_payments.bacs_uuid'
        ];

        const checkRes = await LotsService.checkDpvEligible({
          'uuids': uuids,
          'expand': expands.join(','),
          'auction_uuid': this.tableData.sale ? this.tableData.sale.uuid : null
        });
        if (checkRes.success) {
          const reservations = checkRes.body.reservations;
          if (checkRes.body.warning) {
            let success = await this.$confirm(checkRes.body.warning);
            if (!success) {
              return;
            }
          }
          if (checkRes.body.show_modal) {
            await this.showBatchLotUnsoldModal(reservations);
          } else {
            const isSuccess = await this.$confirm(
              null,
              null,
              null,
              null,
              this.$i18n.t('Are you sure you want to mark selected lots as unsold?'),
              this.$i18n.t('Yes'),
              this.$i18n.t('No'),
              null,
              {
                label: this.$i18n.t("Please type 'confirm' to proceed"),
                text: 'confirm'
              }
            );
            if (isSuccess) {
              const data = reservations.map(res => ({reservation_uuid: res.uuid}));
              await LotsService.sendUnsoldInfo(data);
            }
          }
        } else {
          this.$errorDialog(checkRes.body.error);
        }
      },
      async onBatchRemoveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to delete selected lots?"));
        if (success) {
          let response = await this.batchRemoveReservations(uuids)
          if (response.success) {
            this.$toast('success', this.$i18n.t('Lot(s) were successfully deleted'));
            this.getTableData();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Delete Validation'));
            } else if (response.body.errors) {
              const listItemsArr = [];
              response.body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            }
          }
        }

      },
      async onImagesFileChange($event) {
        this.isUploading = true;
        const failedFilesData = [];
        const successfulFiles = [];
        const files = $event.target.files;
        let sumSize = 0;

        if (files.length > this.bulkImagesMaxFileCount) {
          this.$errorDialog(
            this.$i18n.t("You can not upload more than {count} images.", {count: this.bulkImagesMaxFileCount}),
            this.$i18n.t('Bulk Upload Validation')
          );
          this.isUploading = false;
          this.$refs.imagesFileRef.value = null;
          return;
        }

        for (let file of files) {
          sumSize += file.size;
          const sizeInMbs = file.size / 1000000; // Convert bytes to megabytes
          if (!this.imageFileFormats.includes(file.type)) {
            failedFilesData.push({failure_reason: 'type', file});
          } else if (sizeInMbs > this.imageMaxFileSize) {
            failedFilesData.push({failure_reason: 'size', file});
          } else {
            successfulFiles.push(file);
          }
        }

        const sizeInMbs = sumSize / 1000000; // Convert bytes to megabytes
        if (sizeInMbs > this.imagesZipMaxFileSize) {
          this.$errorDialog(
            this.$i18n.t("Bulk upload size limit exceeded. Max size allowed {size} mbs.", {size: this.imagesZipMaxFileSize}),
            this.$i18n.t('Bulk Upload Validation')
          );
        } else if (failedFilesData.length > 0) {
          Utils.parseFailedImagesData(failedFilesData, this);
        } else {

          let formData = new FormData();

          for (let file of successfulFiles) {
            formData.append('images', file);
          }

          let progress = await this.addProgress(this.$i18n.t("Uploading images"));
          let response = await LotsService.createLotImagesFromBulk(formData, (progressEvent) => {
            let fileProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
            progress.progressValue = fileProgressValue;
            if (fileProgressValue === 100) {
              this.removeProgress(progress.uuid);
            }
          });

          this.handleUploadResponse(response);
        }

        this.isUploading = false;
        this.$refs.imagesFileRef.value = null;
      },
      handleUploadResponse(response) {
        const nonFieldErrors = response.body.non_field_errors;
        if (response.success) {
          if (!nonFieldErrors) {
            this.$toast('success', this.$i18n.t('Images were uploaded successfully'));
          } else {
            this.$toast('success', this.$i18n.t('Images were uploaded partially. Please check error message.'));
          }
        }

        if (nonFieldErrors) {
          if (Array.isArray(nonFieldErrors)) {
            Utils.showErrorsInDialog(nonFieldErrors, this);
          } else {
            const dataArr = [];
            for (let lotNo in nonFieldErrors) {
              const listItemsArr = [];
              for (const imageName in nonFieldErrors[lotNo]) {
                const msg = `${imageName} - ${nonFieldErrors[lotNo][imageName].join(', ')}`;
                listItemsArr.push(this.$createElement('li', {}, msg));
              }
              dataArr.push(this.$createElement('h5', {class: "mb-0"}, `Lot ${lotNo}`));
              dataArr.push(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
            }
            this.$errorDialog(this.$createElement('div', {}, dataArr), this.$i18n.t('Image Validation Error'));
          }
        }
      },
      async onZipFileChange($event) {
        if (!$event.target.files || !$event.target.files[0]) {
          return false;
        }

        this.isUploading = true;

        const zipFile = $event.target.files[0];
        const sizeInMbs = zipFile.size / 1000000; // Convert bytes to megabytes

        if (sizeInMbs > this.imagesZipMaxFileSize) {
          this.$errorDialog(
            this.$i18n.t("Zip file size limit exceeded. Max size allowed {size} mbs.", {size: this.imagesZipMaxFileSize}),
            this.$i18n.t('Zip File Validation')
          );
          this.isUploading = false;
          this.$refs.zipFileRef.value = null;
          return;
        }

        let formData = new FormData();
        formData.append('images', zipFile);

        let progress = await this.addProgress(zipFile.name);
        let response = await LotsService.createLotImagesFromZip(formData, (progressEvent) => {
          this.zipFileProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
          progress.progressValue = this.zipFileProgressValue;
          if (this.zipFileProgressValue === 100) {
            this.removeProgress(progress.uuid);
          }
        });

        this.handleUploadResponse(response);
        this.isUploading = false;
        this.$refs.zipFileRef.value = null;
      },
      async onViewClick(data) {
        this.$router.push({
          path: `/reservations/view/${data.uuid}`
        });
      },
      async onRemoveClick(lot) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete lot {lot}?", {lot: lot.lot_no}),
          this.$i18n.t('Lot {lot} was successfully deleted', {lot: lot.lot_no}),
          this.removeLot,
          lot
        );
      },
      onImagesUploaded(data) {
        this.updateListLotImages(data);
      },
      onLotsImagesSetNameTemplateClick() {
        if (this.amountLotImagesName <= 1) {
          const template = this.auctionImageExportNamingFormat.length === 1
            ? {"file_name_template": this.auctionImageExportNamingFormat[0].value}
            : {"file_name_template": null};
          this.onExportAllImagesClick(template);
        } else {
          this.showLotImagesNameModal({source: SOURCE_LOTS_TABLE, sale: this.tableData.sale});
        }
      },
      onLotUpUsingQrClick() {
        this.showQrLotUpModal({source: SOURCE_LOTS_TABLE, sale: this.tableData.sale})
      },
      onExportAllImagesClick(template) {
        let msg = this.$i18n.t("Export may take time, depending on the size of the data. Are you sure you want to proceed?") + '<br/> <br/>'
        let extraMessage
        if (!this.tableData.sale.is_images_exported) {
          msg += this.$i18n.t("Please click the 'Generate Image Exports' button to create download links for the images.");
        } else {
          msg += this.$i18n.t("Already generated at {date} by {author}", {
            date: this.$options.filters.toDateTime(this.tableData.sale.auction_images_export.created_at),
            author: this.tableData.sale.auction_images_export.created_by.display_name
          });
          extraMessage = '<div class="hover-cursor hover-underline text-primary">' + this.$i18n.t("Download") + '</div>'
        }
        this.$confirm(
          msg,
          this.$i18n.t("Image export links are being generated please check back soon."),
          (event) => {
            return LotsService.getLotsImagesZipData(event).then(() => this.getSale(this.tableData.sale.uuid))
          },
          {template, auctionUuid: this.tableData.sale.uuid},
          i18n.t('Please Confirm'),
          this.tableData.sale.is_images_exported ? 'Regenerate Image Export' : 'Generate Image Exports',
          i18n.t("Cancel"),
          null,
          null,
          extraMessage && {
            message: extraMessage,
            handler: LotsService.getLotsImagesZipData,
            handlerParams: {template: {is_reexport: true}, auctionUuid: this.tableData.sale.uuid},
            successMessage: this.$i18n.t("Image export links are being generated please check back soon.")
          }
        );
      },
      isSelectedExportEnabled(selectedItems) {
        const size = selectedItems ? selectedItems.size : 0;
        return size >= 1 && size <= EXPORT_SELECTED_LOTS_LIMIT;
      },
      exportSelectedTooltip(selectedItems) {
        const size = selectedItems ? selectedItems.size : 0;
        if (size === 0) {
          return this.$i18n.t("Select at least one lot to enable this option.");
        }
        if (size > EXPORT_SELECTED_LOTS_LIMIT) {
          return this.$i18n.t("Select at most {limit} lots.", {limit: EXPORT_SELECTED_LOTS_LIMIT});
        }
        return '';
      },
      onExportSelectedImagesEntryClick(selectedItems) {
        if (!this.isSelectedExportEnabled(selectedItems)) {
          return;
        }
        const items = [...selectedItems];
        const itemsWithoutLot = items.filter(item => !item.lot);
        if (itemsWithoutLot.length > 0) {
          this.$errorDialog(
            this.$i18n.t("Some of the selected items do not have a lot number and cannot be exported. Please deselect them and try again.")
          );
          return;
        }
        const lot_uuids = items.map(item => item.lot.uuid);
        const lot_count = lot_uuids.length;
        if (this.amountLotImagesName <= 1) {
          const template = this.auctionImageExportNamingFormat.length === 1
            ? {"file_name_template": this.auctionImageExportNamingFormat[0].value}
            : {"file_name_template": null};
          this.onExportSelectedImagesClick(template, lot_uuids, lot_count);
        } else {
          this.showLotImagesNameModal({
            source: SOURCE_LOTS_TABLE,
            sale: this.tableData.sale,
            mode: 'selected',
            lot_uuids,
            lot_count,
          });
        }
      },
      onExportSelectedImagesClick(template, lot_uuids, count) {
        const msg = this.$i18n.t(
          "Export may take time, depending on the size of the data. You are about to export images for {count} selected lot(s). Are you sure you want to proceed?",
          {count}
        );
        this.$confirm(
          msg,
          this.$i18n.t("Image export links are being generated please check back soon."),
          (event) => LotsService.getLotsImagesZipData(event),
          {
            template: {...template, lot_uuids},
            auctionUuid: this.tableData.sale.uuid,
          },
          i18n.t('Please Confirm'),
          i18n.t('Generate Image Exports'),
          i18n.t("Cancel"),
        );
      },
      getCurrency(lot) {
        if (this.tableData.sale) {
          return this.tableData.sale.currency.symbol;
        }
        return lot.auction && lot.auction.currency ? lot.auction.currency.symbol : null;
      },
      hideAction(actionName) {
        return this.hideActions.includes(actionName)
      },
      disableButton(items) {
        return items.size === 0
      },
      getAuctionDate(lot, type) {
        if (this.enableContinuousAuction && lot.auction && lot.auction.dates && lot.auction.dates.length) {
          return lot.auction.dates[0][type]
        } else {
          if (lot.auction && lot.auction[type]) {
            return lot.auction[type]
          }
        }
        return null;
      },
      onNegotiationsModalOpenClick(item) {
        this.showNegotiationModal({
          reservation: item,
          sale: this.enableContinuousAuction ? item.auction : this.tableData.sale
        })
      },
      onAssignItemsClick() {
        this.showReserveItemsModal();
      },
      async onGeneratePdfPrintoutsClick(selectedItems) {
        let uuids = [];
        selectedItems.forEach(item => uuids.push(item.uuid));
        if (uuids.length > 50) {
          this.$errorDialog(this.$i18n.t("Only 50 PDF’s can be exported at once, please deselect the items to proceed with the export."));
          return true;
        }
        if (this.oldLotPrintoutsEnabled) {
          this.generateLotPrintoutsLoading = true;
          let url = await LotsService.getLotPrintOuts(uuids.join(","))
          this.generateLotPrintoutsLoading = false;
          if (!url) {
            this.$errorDialog(this.$i18n.t("Error while generating export token. Please, try again later."));
            return true;
          }
          this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
            title: this.$i18n.t('Lot PDF Printouts'),
            data: {pdf_url: url}
          });
        } else {
          EventBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Lot Printout')});
          let response = await LotsService.getLotPrintOutsNew({uuids: uuids.join(",")})
          EventBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...response.body,
            filename: this.$i18n.t(`Lot Printout`)
          })
        }

      },
      canReassign(items) {
        for (let item of items) {
          if (item.status && !this.validReassignStatuses.includes(item.status)) {
            return false;
          }
        }
        return true;
      },
      async reassignReservations(selectedItems) {
        if (this.canReassign(selectedItems)) {
          this.showReassignModal({
            reservations: selectedItems,
            source: SOURCE_LOTS_TABLE,
          });
        } else {
          this.$errorDialog(this.$i18n.t("Only items with 'Reserved', 'Unsold' and 'Withdrawn' statuses can be reassigned to another sale."))
        }
      },
      onBulkAdjustPriceClick(selectedItems) {
        let uuids = [];
        for (let item of selectedItems) {

          if (this.disableItemEditOnSold && item.status === 'sold' && item.auction && item.auction.status === 'completed') {
            this.$errorDialog(
              this.$i18n.t("You cannot adjust price for the items with 'sold' status. Please, deselect sold items and try again."),
              this.$i18n.t("Batch Price Adjustment Validation")
            )
            return
          }

          uuids.push(item.uuid)
        }
        this.showBulkAdjustPriceModal({
          reservations: uuids,
          increment_calculation_source: selectedItems.values().next().value.auction.increment_calculation_source,
          source: SOURCE_LOTS_TABLE
        });
      },
      onBatchUpdateSessionClick(selectedItems) {
        let reservations = []
        for (let r of selectedItems) {
          if (r.status !== 'lotted') {
            this.$errorDialog(
              this.$i18n.t("You can only update sessions for lotted items. Please, select only lotted items and try again."),
              this.$i18n.t("Batch Session Update Validation")
            )
            return
          } else if (this.disableItemEditOnSold && r.status === 'sold' && r.auction && r.auction.status === 'completed') {
            this.$errorDialog(
              this.$i18n.t("You cannot update items with 'sold' status. Please, deselect sold items and try again."),
              this.$i18n.t("Batch Financial Update Validation")
            )
            return
          }
          reservations.push(cloneDeep(r))
        }
        this.showBatchSessionUpdateModal({
          reservations: reservations,
          source: SOURCE_LOTS_TABLE,
          sale: this.tableData.sale
        });
      },
      onBatchFinancialUpdateClick(selectedItems) {
        let reservations = []
        for (let r of selectedItems) {
          if (r.status === 'reserved') {
            this.$errorDialog(
              this.$i18n.t("You cannot update items with 'reserved' status. Please, deselect reserved items and try again."),
              this.$i18n.t("Batch Financial Update Validation")
            )
            return
          } else if (this.disableItemEditOnSold && r.status === 'sold' && r.auction && r.auction.status === 'completed') {
            this.$errorDialog(
              this.$i18n.t("You cannot update items with 'sold' status. Please, deselect sold items and try again."),
              this.$i18n.t("Batch Financial Update Validation")
            )
            return
          }
          reservations.push(cloneDeep(r))
        }
        this.showBatchFinancialUpdateModal({
          reservations: reservations,
          source: SOURCE_LOTS_TABLE
        });
      },
      onBatchUpdateCollectionClick(selectedItems) {
        let reservations = []
        for (let r of selectedItems) {
          if (r.status === 'reserved') {
            this.$errorDialog(
              this.$i18n.t("You can not update collection for reserved items. Please, remove reserved items and try again."),
              this.$i18n.t("Batch Collection Update Validation")
            )
            return
          } else if (this.disableItemEditOnSold && r.status === 'sold' && r.auction && r.auction.status === 'completed') {
            this.$errorDialog(
              this.$i18n.t("You cannot update items with 'sold' status. Please, deselect sold items and try again."),
              this.$i18n.t("Batch Financial Update Validation")
            )
            return
          }
          reservations.push(cloneDeep(r))
        }
        this.showBatchCollectionUpdateModal({
          reservations: reservations,
          source: SOURCE_LOTS_TABLE,
          sale: this.tableData.sale
        });
      },
      onBatchHideShowClick(selectedItems) {
        let reservationUuids = []
        for (let r of selectedItems) {
          if (r.status === 'reserve') {
            this.$errorDialog(
              this.$i18n.t("You can not update hide property for reserved items. Please, deselect reserved items and try again."),
              this.$i18n.t("Batch Hide Validation")
            )
            return
          } else if (this.disableItemEditOnSold && r.status === 'sold' && r.auction && r.auction.status === 'completed') {
            this.$errorDialog(
              this.$i18n.t("You cannot update items with 'sold' status. Please, deselect sold items and try again."),
              this.$i18n.t("Batch Financial Update Validation")
            )
            return
          }
          reservationUuids.push(r.uuid)
        }

        this.$confirm(
          this.$i18n.t("Are you sure you want to update 'Hide Lot' property for selected lots?"),
          this.$i18n.t("Lots were successfully updated"),
          this.batchUpdateHideLot,
          {reservation_uuids: reservationUuids, hide_lot: false},
          this.$i18n.t("Batch Hide/Show Lots"),
          i18n.t("Show"),
          i18n.t("Cancel"),
          [{
            handler: this.batchUpdateHideLot,
            handlerParams: {reservation_uuids: reservationUuids, hide_lot: true},
            submitBtnText: i18n.t("Hide"),
            successMessage: this.$i18n.t("Lots were successfully updated")
          }]
        );
      },
      async onInventoryOverviewExportClick(items) {
        let payload = {'uuids': [...items].map(item => item.inventory_uuid).join(',')};

        if (Object.keys(this.locales.available_locales).length === 1) {
          let success = await this.$confirm(this.$i18n.t("Are you sure you want to export inventory overview with following filters?"))
          if (!success) {
            return;
          }

          let response = await InventoryService.exportInventoryOverview({
            ...payload, language: this.locales.master_language.key
          })
          if (response.success) {
            this.$toast('success', this.$i18n.t("Inventory overview export has successfully started."))
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0])
            } else {
              this.$errorDialog(this.$i18n.t("Something went wrong. Please, try again later"))
            }
          }
        } else {
          this.showInventoryOverviewGenerationModal({payload: payload})
        }
      },
      async onNewCondRequestClick(items) {
        this.showConditionalReportRequestModal({reservations: Array.from(items)})
      },
      clearSelected() {
        if (this.$refs.lotTable) {
          this.$refs.lotTable.clearSelected()
        }
      },
      async onBatchWithdrawClick(items) {
        const reservations = Array.from(items)
        for (const item of reservations) {
          if (item.lot && ['sold', 'withdrawn'].includes(item.lot.status) || item.status === 'reserved') {
            this.$toast('danger', this.$i18n.t("You cannot withdraw lots with 'Sold', 'Withdrawn' or 'Reserved' status."));
            return
          }
        }

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to withdraw selected lots?'),
          null, null, null, this.$i18n.t('Batch Withdraw Lots'));
        if (!success) {
          return
        }

        const uuids = reservations.map(el => el.uuid)
        const response = await LotsService.batchWithdrawReservation(uuids)

        if (response.success) {
          this.$toast('success', this.$i18n.t('Lots have been successfully withdrawn.'));
          this.getTableData()
        } else {
          this.$toast('danger', this.$i18n.t('Error occurred while withdrawing lots.'));
        }
      },
      async onViewDpvPdfClick(dpvUuid) {
        await DpvService.getDpvPdfUrl(this, dpvUuid);
      },
      selectedItemsAllChange(selectedItemsAll) {
        this.$emit('selectedItemsAllChange', selectedItemsAll);
      },
    },

    mounted() {
      this.getTableData();
      this.$ws.listen(`reservations.${WS_BULK_ADJUST_PRICES}`, async (data) => {
        await this.addProgressIfNotExist('Price Adjustment Progress', 'priceAdjustmentProgress');
        await this.handleProgressNotification(
          data,
          this.$i18n.t("Price Adjustment is finished"),
          this.$i18n.t('Price Adjustment'),
          this.$i18n.t('Error while handling price adjustment. Please, try again later'),
          'priceAdjustmentProgress'
        )
      });
      this.$ws.listen(`reservations.${WS_INVENTORY_BATCH_LOT_UP_PROGRESS}`, async (data) => {
        await this.addProgressIfNotExist('Lot Up', 'batchLotUpProgress');
        this.handleProgressNotification(
          data,
          this.$i18n.t("Lot up successfully finished"),
          this.$i18n.t('Lot Up'),
          this.$i18n.t('Error while handling lot up. Please, try again later'),
          'batchLotUpProgress'
        )
      });
      this.$ws.listen(`reservations.${WS_LOTS_BATCH_MARK_UNSOLD_PROGRESS}`, async (data) => {
        await this.addProgressIfNotExist('Mark unsold', 'batchMarkUnsoldProgress');
        this.handleProgressNotification(
          data,
          this.$i18n.t('Mark unsold successfully finished'),
          this.$i18n.t('Batch Mark Unsold'),
          this.$i18n.t('Error while batch marking lots as unsold. Please, try again later'),
          'batchMarkUnsoldProgress'
        )
      });
    },
    beforeMount() {
      EventBus.$on(LOTS_TABLE_CLEAR_SELECTED, () => this.clearSelected());
    },
    destroyed() {
      this.removeProgressIfExist();
      this.removeProgressIfExist('batchLotUpProgress');
      this.removeProgressIfExist('batchMarkUnsoldProgress');
      this.$ws.remove(`reservations.${WS_BULK_ADJUST_PRICES}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
      .stock-no-col {
        min-width: 150px;
        width: 170px;
      }

      .title-col {
        min-width: 320px;
        text-wrap: wrap;
      }

      .description-col {
        min-width: 320px;
        max-width: 500px;
        text-wrap: wrap;
      }

      .estimate-col {
        min-width: 160px;
        width: 180px;
      }

      .quantity-col {
        min-width: 90px;
        width: 110px;
      }

      .reserve-col {
        min-width: 80px;
        width: 100px;
      }

      .status-col {
        min-width: 100px;
        width: 120px;
      }

      .lot-no-col {
        min-width: 60px;
        width: 80px;
      }

      .winning-bidder-col {
        min-width: 140px;
        width: 160px;
      }

      .dates-col {
        min-width: 300px;
      }

      .invoice-col {
        min-width: 100px;
        width: 120px;
      }

      .account-managers-col {
        min-width: 140px;
      }

      .customer-col {
        min-width: 140px;
      }

      .negotiation-status-col {
        min-width: 150px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .sale-no-col {
        min-width: 120px;
      }
    }
  }

  .invoice {
    cursor: pointer;
  }

  /deep/ .dropdown-menu.show {
    max-height: 280px;
    overflow: auto;
  }

</style>

